import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, AlertTriangle, Info } from 'lucide-react';

const Troubleshooting = () => {
  const troubleshootingData = [
    {
      observation: 'Crémage rapide',
      cause: 'Cible HLB décalée ou viscosité de phase continue insuffisante',
      levier:
        'Revenir au calcul, élargir le screening de ±0,5 et renforcer le réseau (alcool gras, polymère, cire)',
      severity: 'high',
    },
    {
      observation: 'Déphasage à chaud',
      cause: 'Film interfacial trop faible ou choix de famille inadéquat',
      levier:
        'Changer de couple, augmenter légèrement le co-émulsionnant ou basculer vers un système lamellaire plus robuste',
      severity: 'high',
    },
    {
      observation: 'Blanchiment / soaping',
      cause: 'Charge émulsionnante trop élevée ou structuration trop cristalline',
      levier:
        'Réduire l’émulsionnant total, ajuster alcool gras / cire, introduire des émollients plus glissants',
      severity: 'medium',
    },
    {
      observation: 'Toucher collant',
      cause: 'Excès d’émulsionnants très hydrophiles',
      levier:
        'Rééquilibrer vers plus de phase grasse légère ou vers une famille plus sensorielle',
      severity: 'medium',
    },
    {
      observation: 'Perte de viscosité avec parfum ou électrolytes',
      cause: 'Famille trop sensible au milieu',
      levier:
        'Tester un non ionique plus robuste, un phosphate ou un système pré-mélangé mieux tolérant',
      severity: 'medium',
    },
    {
      observation: 'Séparation de phase à froid',
      cause: 'Cristallisation excessive des corps gras ou émulsionnants',
      levier:
        'Ajuster le ratio alcools gras/cires, intégrer des émollients fluides, optimiser la température de stockage',
      severity: 'high',
    },
    {
      observation: 'Texture granuleuse',
      cause: 'Recristallisation de certains composants ou taille de gouttes hétérogène',
      levier:
        'Améliorer le procédé d’émulsification (température, vitesse), vérifier la compatibilité des phases',
      severity: 'low',
    },
    {
      observation: 'Formation de mousse excessive',
      cause: 'Émulsionnants à fort pouvoir moussant ou cisaillement trop intense',
      levier:
        'Sélectionner des émulsionnants à faible HLB pour le type visé, réduire la vitesse d’agitation, ajouter un agent antimousse',
      severity: 'low',
    },
  ];

  const recommendationsData = [
    {
      title: 'Plan d’essais minimum',
      items: [
        'Calculer le HLB requis à chaque modification de la phase grasse',
        'Prévoir au minimum trois cibles interfaciales : HLB théorique -0,5 ; HLB théorique ; HLB théorique +0,5',
        'Tester au moins deux charges d’émulsionnant total et une variante de co-structurant',
        'Évaluer à J0, J1, après centrifugation, puis en vieillissement (40-45°C, 4°C, cycles chaud/froid)',
      ],
    },
    {
      title: 'Paramètres à documenter',
      items: [
        'Viscosité (Brookfield, cône-plan)',
        'Brillance et blanchiment visuel',
        'Sensation collante et étalement',
        'Granulométrie (microscopie optique ou diffusion laser)',
        'Compatibilité pH, électrolytes, parfum',
      ],
    },
    {
      title: 'Facteurs clés de stabilité',
      items: [
        'Viscosité de la phase continue',
        'Distribution granulométrique des gouttes',
        'Architecture interfaciale (film, réseau lamellaire)',
        'Présence d’alcools gras, cires, beurres, polymères',
        'Gestion des électrolytes, pH, parfums, extraits',
      ],
    },
  ];

  const getSeverityColor = (severity) => {
    const colors = {
      high: 'bg-[#B94A3E]/10 border-[#B94A3E]/20 text-[#B94A3E]',
      medium: 'bg-[#2F4840]/10 border-[#2F4840]/20 text-[#2F4840]',
      low: 'bg-[#346A53]/10 border-[#346A53]/20 text-[#346A53]',
    };
    return colors[severity] || colors.medium;
  };

  const getSeverityIcon = (severity) => {
    if (severity === 'high') return <AlertCircle className="h-4 w-4" />;
    if (severity === 'medium') return <AlertTriangle className="h-4 w-4" />;
    return <Info className="h-4 w-4" />;
  };

  const getSeverityLabel = (severity) => {
    const labels = {
      high: 'Critique',
      medium: 'Modéré',
      low: 'Mineur',
    };
    return labels[severity] || 'Modéré';
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-4xl font-semibold heading-font text-[#1A1C1B]" data-testid="troubleshooting-title">
          Guide de Dépannage
        </h2>
        <p className="text-[#5C615E] mt-2">
          Diagnostics et solutions pour les problèmes de stabilité
        </p>
      </div>

      <div>
        <h3 className="text-2xl font-medium heading-font text-[#1A1C1B] mb-4">
          Tableau de dépannage rapide
        </h3>
        <div className="space-y-3">
          {troubleshootingData.map((item, index) => (
            <Card
              key={index}
              className={`border ${getSeverityColor(item.severity).split(' ')[1]}`}
              data-testid={`troubleshoot-item-${index}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-sm flex-shrink-0 ${getSeverityColor(
                      item.severity
                    )}`}
                  >
                    {getSeverityIcon(item.severity)}
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <h4 className="font-semibold text-[#1A1C1B] text-lg">{item.observation}</h4>
                      <span
                        className={`px-2 py-1 rounded-sm text-xs font-medium ${getSeverityColor(
                          item.severity
                        )}`}
                      >
                        {getSeverityLabel(item.severity)}
                      </span>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[#5C615E] mb-1">
                          Cause probable
                        </p>
                        <p className="text-sm text-[#1A1C1B]">{item.cause}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-[#5C615E] mb-1">
                          Levier d'action
                        </p>
                        <p className="text-sm text-[#1A1C1B]">{item.levier}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-2xl font-medium heading-font text-[#1A1C1B] mb-4">
          Recommandations R&D
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          {recommendationsData.map((section, index) => (
            <Card key={index} className="border-[#E5E5DF]" data-testid={`recommendation-${index}`}>
              <CardHeader>
                <CardTitle className="text-lg font-medium text-[#1A1C1B]">{section.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-2 text-sm text-[#1A1C1B]">
                      <span className="text-[#2F4840] font-bold flex-shrink-0">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Card className="border-[#E5E5DF] bg-[#E4EDE9]">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-[#2F4840] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-[#1A1C1B] mb-2">
                Rappel important
              </h4>
              <p className="text-sm text-[#1A1C1B] leading-relaxed">
                Le HLB n'est pas un verdict absolu, mais un bon point de départ pour réduire les essais.
                La stabilité longue durée dépend de nombreux facteurs (viscosité, granulométrie, architecture
                interfaciale, procédé). Toujours valider par screening à plusieurs points et conditions
                de vieillissement.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Troubleshooting;
